import { PanelAction } from '@/components/Layout/Panel/Panel';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Bell, BellOff } from 'lucide-react';
import { useMemo } from 'react';
import { docsMutations, docsQueries } from './docs.queries';

/**
 * Project-panel action that toggles the current user's subscription to
 * email notifications for the project's docs catalog.
 *
 * The current user is derived server-side from the JWT (see
 * api.subscribe_catalog / api.unsubscribe_catalog RPCs); the client never
 * supplies a user id.
 *
 * Returns null until the catalog (and thus its id) is known, to avoid
 * flicker on first render.
 */
export function useDocsNotificationAction(
  projectId: number,
): PanelAction | null {
  const { data: rows } = useQuery(docsQueries.byProject(projectId));
  const catalogId = useMemo(() => rows?.[0]?.catalog_id ?? null, [rows]);

  const { data: isSubscribed, isError } = useQuery({
    ...docsQueries.subscription(catalogId ?? 0),
    enabled: !!catalogId,
    // Auxiliary UI state: the subscription RPC is online-only (pass-through
    // in the SW). An unknown subscription state must not error the whole
    // docs tab — the action hides instead.
    throwOnError: false,
    retry: false,
  });

  const subscribe = useMutation(docsMutations.subscribe());
  const unsubscribe = useMutation(docsMutations.unsubscribe());

  const inFlight = subscribe.isPending || unsubscribe.isPending;

  // Memo keeps the PanelAction reference stable so consumer `useMemo`
  // chains don't invalidate every render.
  return useMemo<PanelAction | null>(() => {
    if (catalogId === null || isError) return null;
    return {
      icon: isSubscribed ? BellOff : Bell,
      label: isSubscribed
        ? 'Unsubscribe from doc updates'
        : 'Subscribe to doc updates',
      disabled: inFlight,
      onCalled: () => {
        if (isSubscribed) unsubscribe.mutate({ catalogId });
        else subscribe.mutate({ catalogId });
      },
    };
  }, [catalogId, isError, isSubscribed, inFlight, subscribe, unsubscribe]);
}
